import React, { useState, useEffect } from 'react';
import TeacherAssignmentsService from '../../services/TeacherAssignmentsService';
import ClassParserService from '../../services/ClassParserService';
import { classFilesData } from '../../Data/classFilesData';
import { supabase } from '../../lib/supabase';
import './ClassTeachersView.css';

const ClassTeachersView = ({ tenantId, onBack }) => {
  const [availableClasses, setAvailableClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [classTeachers, setClassTeachers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [showAddSubjectModal, setShowAddSubjectModal] = useState(false);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [subjectName, setSubjectName] = useState('');
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    console.log('ClassTeachersView mounted with tenantId:', tenantId);
    if (!tenantId) {
      setError('tenantId is required but not provided');
      return;
    }
    loadAvailableClasses();
  }, [tenantId]);

  const loadAvailableClasses = () => {
    const classes = Object.keys(classFilesData).map(className => ({
      name: className,
      displayName: className.replace('Class ', '')
    }));
    setAvailableClasses(classes);
  };

  const handleClassSelect = async (className) => {
    try {
      setLoading(true);
      setError('');
      setSelectedClass(className);
      
      // Validate tenantId
      if (!tenantId) {
        throw new Error('tenantId is required to load class teachers');
      }

      console.log('Loading class teachers for:', className, 'with tenantId:', tenantId);
      
      // First try to get teachers from saved assignments
      console.log('Fetching class teachers from saved assignments...');
      const classTeachersData = await TeacherAssignmentsService.getClassTeachers(tenantId, className);
      console.log('Class teachers from assignments:', classTeachersData);
      
      if (classTeachersData && classTeachersData.length > 0) {
        // Use saved assignments data
        console.log('Class teachers data from assignments:', classTeachersData);
        console.log('Sample teacher data:', classTeachersData[0]);
        setClassTeachers(classTeachersData);
        setMessage(`Loaded ${classTeachersData.length} teachers for ${className} from saved assignments`);
      } else {
        // Fallback to parsing class files if no saved assignments
        console.log('No saved assignments found, parsing class file...');
        const classData = classFilesData[className];
        if (!classData) {
          throw new Error(`Class data not found for ${className}`);
        }

        // Parse the class data to extract teacher information
        const parsedClassData = await ClassParserService.parseClassFile(classData, className, tenantId);
        console.log('Parsed class data:', parsedClassData);
        
        // Get teacher code mapping
        const teacherMapping = await TeacherAssignmentsService.getTeacherCodeMapping(tenantId);
        console.log('Teacher mapping:', teacherMapping);
        
        // Map teacher codes to full teacher information
        const mappedTeachers = parsedClassData.map(assignment => {
          const teacherInfo = teacherMapping[assignment.assignment_data?.teacher_code];
          
          return {
            ...assignment,
            teacher_name: teacherInfo ? teacherInfo.name : assignment.assignment_data?.teacher_name || 'Unknown Teacher',
            teacher_english_name: teacherInfo ? teacherInfo.english_name : '',
            teacher_department: teacherInfo ? teacherInfo.department : '',
            teacher_id: teacherInfo ? teacherInfo.id : assignment.teacher_id
          };
        });

        setClassTeachers(mappedTeachers);
        setMessage(`Loaded ${mappedTeachers.length} teachers for ${className} from class file`);
      }
    } catch (error) {
      console.error('Error loading class teachers:', error);
      setError(`Failed to load class teachers: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const getAssignmentTypeDisplay = (assignmentType) => {
    const typeMap = {
      'subject_teacher': 'Subject Teacher',
      'class_master': 'Class Master',
      'vice_class_master': 'Vice Class Master',
      'committee_member': 'Committee Member',
      'department_head': 'Department Head'
    };
    return typeMap[assignmentType] || assignmentType;
  };

  const getAssignmentTypeColor = (assignmentType) => {
    const colorMap = {
      'subject_teacher': '#667eea',
      'class_master': '#28a745',
      'vice_class_master': '#17a2b8',
      'committee_member': '#ffc107',
      'department_head': '#dc3545'
    };
    return colorMap[assignmentType] || '#6c757d';
  };

  const getRolePriority = (assignmentType) => {
    const priorityMap = {
      'class_master': 1,
      'vice_class_master': 2,
      'subject_teacher': 3,
      'committee_member': 4,
      'department_head': 5
    };
    return priorityMap[assignmentType] || 6;
  };

  const handleAddSubject = (teacher) => {
    setSelectedTeacher(teacher);
    setSubjectName(teacher.subject_name || '');
    setShowAddSubjectModal(true);
  };

  const handleUpdateSubject = async () => {
    if (!selectedTeacher || !subjectName.trim()) {
      setError('Please enter a subject name');
      return;
    }

    try {
      setUpdating(true);
      setError('');

      // Update the assignment in the database
      const { data, error } = await supabase
        .from('teacher_assignments')
        .update({ subject_name: subjectName.trim() })
        .eq('id', selectedTeacher.id)
        .select();

      if (error) {
        throw new Error(`Failed to update subject: ${error.message}`);
      }

      // Update the local state
      setClassTeachers(prevTeachers => 
        prevTeachers.map(teacher => 
          teacher.id === selectedTeacher.id 
            ? { ...teacher, subject_name: subjectName.trim() }
            : teacher
        )
      );

      setMessage(`Successfully updated subject for ${selectedTeacher.teacher_name}`);
      setShowAddSubjectModal(false);
      setSelectedTeacher(null);
      setSubjectName('');

    } catch (err) {
      setError(`Failed to update subject: ${err.message}`);
      console.error('Error updating subject:', err);
    } finally {
      setUpdating(false);
    }
  };

  const handleCancelAddSubject = () => {
    setShowAddSubjectModal(false);
    setSelectedTeacher(null);
    setSubjectName('');
  };

  // Sort teachers by role priority
  const sortedClassTeachers = (classTeachers || []).sort((a, b) => {
    // Ensure both objects exist
    if (!a || !b) return 0;
    
    const priorityA = getRolePriority(a.assignment_type);
    const priorityB = getRolePriority(b.assignment_type);
    if (priorityA !== priorityB) {
      return priorityA - priorityB;
    }
    // Handle undefined teacher_name
    const nameA = a.teacher_name || '';
    const nameB = b.teacher_name || '';
    return nameA.localeCompare(nameB);
  });

  // Show error if tenantId is missing
  if (!tenantId) {
    return (
      <div className="class-teachers-view">
        <div className="view-header">
          <h3>🏫 Class Teachers View</h3>
          <p>View all teachers and their roles for each class</p>
          <button className="back-btn" onClick={onBack}>
            ← Back to Teachers
          </button>
        </div>
        <div className="error-message">
          ❌ tenantId is required but not provided. Please check the configuration.
        </div>
      </div>
    );
  }

  return (
    <div className="class-teachers-view">
      <div className="view-header">
        <h3>🏫 Class Teachers View</h3>
        <p>View all teachers and their roles for each class</p>
        <button className="back-btn" onClick={onBack}>
          ← Back to Teachers
        </button>
      </div>

      {error && (
        <div className="error-message">
          ❌ {error}
        </div>
      )}

      {message && (
        <div className="success-message">
          ✅ {message}
        </div>
      )}

      <div className="view-content">
        {/* Classes List */}
        <div className="classes-list">
          <h4>📚 Available Classes ({availableClasses.length})</h4>
          <div className="classes-grid">
            {availableClasses.map((classItem) => (
              <div
                key={classItem.name}
                className={`class-card ${selectedClass === classItem.name ? 'selected' : ''}`}
                onClick={() => handleClassSelect(classItem.name)}
              >
                <div className="class-info">
                  <h5>{classItem.displayName}</h5>
                  <p>Click to view teachers</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Class Teachers Details */}
        {selectedClass && (
          <div className="class-teachers-details">
            <div className="class-header">
              <h4>🏫 {selectedClass}</h4>
              <p>Teachers and their roles in this class</p>
            </div>

            {loading ? (
              <div className="loading">Loading teachers...</div>
            ) : classTeachers && classTeachers.length > 0 ? (
              <div className="class-teachers-section">
                <h5>Class Teachers ({sortedClassTeachers.length})</h5>
                <div className="class-teachers-list">
                  {sortedClassTeachers.map((teacher, index) => (
                    <div key={index} className="class-teacher-item">
                      <div className="teacher-role-info">
                        <span
                          className="teacher-role-badge"
                          style={{ backgroundColor: getAssignmentTypeColor(teacher.assignment_type) }}
                        >
                          {getAssignmentTypeDisplay(teacher.assignment_type)}
                        </span>
                        <div className="teacher-details">
                          <h6>{teacher.teacher_name || 'Unknown Teacher'}</h6>
                          {teacher.teacher_english_name && (
                            <p className="teacher-english">{teacher.teacher_english_name}</p>
                          )}
                          {teacher.teacher_department && (
                            <p className="teacher-dept">{teacher.teacher_department}</p>
                          )}
                        </div>
                      </div>
                      <div className="subject-info">
                        {teacher.subject_name && teacher.subject_name.trim() !== '' && (
                          <span className="subject-badge">{teacher.subject_name}</span>
                        )}
                        {teacher.class_id && (
                          <span className="class-badge">{teacher.class_id}</span>
                        )}
                        {/* Show role-specific information */}
                        {teacher.assignment_type === 'class_master' && (
                          <span className="role-badge class-master-role">Class Management</span>
                        )}
                        {teacher.assignment_type === 'vice_class_master' && (
                          <span className="role-badge vice-class-master-role">Assistant Class Management</span>
                        )}
                        {/* Add subject button for subject teachers without subject name */}
                        {teacher.assignment_type === 'subject_teacher' && (!teacher.subject_name || teacher.subject_name.trim() === '') && (
                          <button 
                            className="add-subject-btn"
                            onClick={() => handleAddSubject(teacher)}
                            title="Add subject information"
                          >
                            Subject Taught Found. ADD
                          </button>
                        )}
                        {/* Debug info - remove in production */}
                        {process.env.NODE_ENV === 'development' && (
                          <div style={{fontSize: '10px', color: '#666', marginTop: '2px'}}>
                            Debug: subject_name="{teacher.subject_name}" (type: {typeof teacher.subject_name}), 
                            assignment_type="{teacher.assignment_type}", 
                            should_show_subject: {teacher.subject_name && teacher.subject_name.trim() !== '' ? 'YES' : 'NO'}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="no-teachers">
                <p>No teachers found for this class.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add Subject Modal */}
      {showAddSubjectModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Add Subject Information</h3>
              <button 
                className="close-btn" 
                onClick={handleCancelAddSubject}
                disabled={updating}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Teacher:</label>
                <div className="teacher-info">
                  <strong>{selectedTeacher?.teacher_name}</strong>
                  <span className="teacher-role">
                    {getAssignmentTypeDisplay(selectedTeacher?.assignment_type)}
                  </span>
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="subjectName">Subject Name:</label>
                <input
                  id="subjectName"
                  type="text"
                  value={subjectName}
                  onChange={(e) => setSubjectName(e.target.value)}
                  placeholder="Enter subject name (e.g., 數學, 英文, 中文)"
                  className="form-input"
                  disabled={updating}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="cancel-btn" 
                onClick={handleCancelAddSubject}
                disabled={updating}
              >
                Cancel
              </button>
              <button 
                className="save-btn" 
                onClick={handleUpdateSubject}
                disabled={updating || !subjectName.trim()}
              >
                {updating ? 'Updating...' : 'Update Subject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClassTeachersView;
